import { createContext, useContext, useState, useCallback, ReactNode } from "react";

export type EasyModeTransactionModal = "contra" | "payment" | "receipt" | "sales" | "purchase" | null;

export interface EasyModeModalMeta {
  defaultTransferType?: "deposit" | "withdrawal" | "transfer";
}

interface EasyModeContextType {
  isEasyMode: boolean;
  toggleEasyMode: () => void;
  enableEasyMode: () => void;
  disableEasyMode: () => void;
  focusedIndex: number;
  setFocusedIndex: (i: number) => void;
  showCommandPalette: boolean;
  setShowCommandPalette: (v: boolean) => void;
  activeModal: EasyModeTransactionModal;
  modalMeta: EasyModeModalMeta;
  openTransactionModal: (modal: Exclude<EasyModeTransactionModal, null>, meta?: EasyModeModalMeta) => void;
  closeTransactionModal: () => void;
}

const EasyModeContext = createContext<EasyModeContextType | null>(null);

export function EasyModeProvider({ children }: { children: ReactNode }) {
  const [isEasyMode, setIsEasyMode] = useState(() => {
    return localStorage.getItem("accoupro-easy-mode") === "true";
  });
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [activeModal, setActiveModal] = useState<EasyModeTransactionModal>(null);
  const [modalMeta, setModalMeta] = useState<EasyModeModalMeta>({});

  const enableEasyMode = useCallback(() => {
    setIsEasyMode(true);
    localStorage.setItem("accoupro-easy-mode", "true");
  }, []);

  const disableEasyMode = useCallback(() => {
    setIsEasyMode(false);
    localStorage.setItem("accoupro-easy-mode", "false");
    setShowCommandPalette(false);
    setActiveModal(null);
    setModalMeta({});
  }, []);

  const toggleEasyMode = useCallback(() => {
    if (isEasyMode) disableEasyMode();
    else enableEasyMode();
  }, [isEasyMode, enableEasyMode, disableEasyMode]);

  const openTransactionModal = useCallback((modal: Exclude<EasyModeTransactionModal, null>, meta?: EasyModeModalMeta) => {
    setActiveModal((current) => {
      // Prevent duplicate modal opening if already open
      if (current === modal) return current;
      return modal;
    });
    setModalMeta(meta || {});
    setShowCommandPalette(false);
  }, []);

  const closeTransactionModal = useCallback(() => {
    setActiveModal(null);
    setModalMeta({});
  }, []);

  return (
    <EasyModeContext.Provider value={{
      isEasyMode, toggleEasyMode, enableEasyMode, disableEasyMode,
      focusedIndex, setFocusedIndex,
      showCommandPalette, setShowCommandPalette,
      activeModal, modalMeta,
      openTransactionModal, closeTransactionModal,
    }}>
      {children}
    </EasyModeContext.Provider>
  );
}

export function useEasyMode() {
  const ctx = useContext(EasyModeContext);
  if (!ctx) throw new Error("useEasyMode must be used within EasyModeProvider");
  return ctx;
}
